/**
 * The eight initial "Looks" — curated design presets a tenant picks from.
 *
 * Each preset bundles a header variant, hero variant, footer variant, plus
 * the CSS-driven knobs (radius, density, button/card style) and curated
 * typography + palette families. Tenants can override any individual knob
 * via `theme_config.look.overrides`.
 *
 * Adding a Look: extend `PresetId` in `./types.ts`, add an entry here, add
 * a display label in `LOOK_LABELS`, a mood in `./moods.ts`, and (optionally)
 * wire a siteType default in `DEFAULT_LOOK_FOR_SITE_TYPE`. The admin Look
 * picker grid auto-includes every entry in `LOOKS`.
 *
 * These eight are pinned by `web/src/looks/__tests__/registry-snapshot.test.ts`:
 * changing a knob here changes every tenant on that Look.
 */

import type { LookPreset, PresetId } from './types';
import type { SiteType } from '../site-type';

export const LOOKS: Record<PresetId, LookPreset> = {
  editorial_boutique: {
    header_variant: 'centered_logo',
    hero_variant: 'split',
    footer_variant: 'multi_column',
    button_shape: 'rounded',
    card_style: 'flat',
    radius_scale: 'soft',
    density: 'spacious',
    font_pair_id: 'editorial',
    palette_family_id: 'editorial_ivory',
  },

  cozy_boutique: {
    header_variant: 'classic',
    hero_variant: 'standard',
    footer_variant: 'multi_column',
    button_shape: 'rounded',
    card_style: 'elevated',
    radius_scale: 'round',
    density: 'comfortable',
    font_pair_id: 'cozy_warm',
    palette_family_id: 'warm_earth',
  },

  modern_minimal: {
    header_variant: 'classic',
    hero_variant: 'split',
    footer_variant: 'compact',
    button_shape: 'square',
    card_style: 'bordered',
    radius_scale: 'sharp',
    density: 'compact',
    font_pair_id: 'modern_sans',
    palette_family_id: 'monochrome',
  },

  bold_marketplace: {
    header_variant: 'transparent_overlay',
    hero_variant: 'fullscreen_media',
    footer_variant: 'big_cta',
    button_shape: 'square',
    card_style: 'elevated',
    radius_scale: 'sharp',
    density: 'comfortable',
    font_pair_id: 'bold_grotesk',
    palette_family_id: 'vibrant',
  },

  hospitality_warm: {
    header_variant: 'centered_logo',
    hero_variant: 'search_forward',
    footer_variant: 'multi_column',
    button_shape: 'rounded',
    card_style: 'elevated',
    radius_scale: 'round',
    density: 'spacious',
    font_pair_id: 'cozy_warm',
    palette_family_id: 'warm_earth',
  },

  cultural_classic: {
    header_variant: 'mega_menu',
    hero_variant: 'centered_card',
    footer_variant: 'big_cta',
    button_shape: 'rounded',
    card_style: 'bordered',
    radius_scale: 'soft',
    density: 'comfortable',
    font_pair_id: 'museum',
    palette_family_id: 'jewel',
  },

  playful_pop: {
    header_variant: 'transparent_overlay',
    hero_variant: 'fullscreen_media',
    footer_variant: 'minimal',
    button_shape: 'pill',
    card_style: 'elevated',
    radius_scale: 'pill',
    density: 'comfortable',
    font_pair_id: 'playful',
    palette_family_id: 'vibrant',
  },

  tech_saas: {
    header_variant: 'mega_menu',
    hero_variant: 'centered_card',
    footer_variant: 'minimal',
    button_shape: 'square',
    card_style: 'glass',
    radius_scale: 'sharp',
    density: 'compact',
    font_pair_id: 'tech',
    palette_family_id: 'gradient_tech',
  },
};

/**
 * Human-readable label + one-line description for the admin Look picker.
 * The description is also what the wizard's AI look-picker reads, verbatim,
 * when it chooses a Look for a new tenant — so phrasing here is phrasing the
 * model matches a brand against.
 */
export const LOOK_LABELS: Record<PresetId, { label: string; description: string }> = {
  editorial_boutique: {
    label: 'Editorial Boutique',
    description: 'Magazine-style layout with generous whitespace, centered logo, and split hero. Good for fashion, lifestyle, premium retail.',
  },
  cozy_boutique: {
    label: 'Cozy Boutique',
    description: 'Warm cream + terracotta palette, soft serif headings, classic top-nav. Good for garden, food, artisan ecommerce.',
  },
  modern_minimal: {
    label: 'Modern Minimal',
    description: 'Tight grid, monochrome palette, sharp corners, sans-only typography. Good for B2B SaaS, tech retail.',
  },
  bold_marketplace: {
    label: 'Bold Marketplace',
    description: 'Saturated palette, transparent overlay header, fullscreen-media hero. Good for electronics, deals, big-catalog retail.',
  },
  hospitality_warm: {
    label: 'Hospitality Warm',
    description: 'Search-forward hero with availability search, rounded everything, spacious feel. Good for lodging, B&Bs, vacation rentals.',
  },
  cultural_classic: {
    label: 'Cultural Classic',
    description: 'Ivory + jewel accents, mega-menu, centered-card hero, Donate CTA in footer. Good for museums, nonprofits, institutions.',
  },
  playful_pop: {
    label: 'Playful Pop',
    description: 'Vibrant colors, pill buttons, rounded sans display, fullscreen-media hero. Good for entertainment, kids, social casino.',
  },
  tech_saas: {
    label: 'Tech SaaS',
    description: 'Dark-mode-first, gradient hero, glass cards, mega-menu, minimal footer. Good for SaaS, devtools, B2B marketing.',
  },
};

/**
 * SiteType → recommended preset, used by:
 *   1. The wizard's AI Look picker as a fallback when the LLM call fails.
 *   2. `db-setup.ts` when the look-picker step did not run at all.
 *   3. `resolveLook` when a tenant has no `theme_config.look` yet.
 *
 * Picked to MATCH each siteType's original visual style, so the one-time
 * backfill produced zero pixel diff.
 */
export const DEFAULT_LOOK_FOR_SITE_TYPE: Record<SiteType, PresetId> = {
  ecommerce: 'cozy_boutique',
  saas: 'tech_saas',
  museum: 'cultural_classic',
  lodging: 'hospitality_warm',
  social_casino: 'playful_pop',
};

/** The preset used when the site type is unknown or unset. */
export const FALLBACK_PRESET_ID: PresetId = 'cozy_boutique';

export const ALL_PRESET_IDS: readonly PresetId[] = Object.keys(LOOKS) as PresetId[];

export function isPresetId(value: unknown): value is PresetId {
  return typeof value === 'string' && (ALL_PRESET_IDS as readonly string[]).includes(value);
}

/**
 * The preset a site of this type starts on. Accepts any string because the
 * callers (wizard state, settings JSON) carry an unvalidated site type.
 */
export function defaultPresetForSiteType(siteType: string | null | undefined): PresetId {
  return (siteType && (DEFAULT_LOOK_FOR_SITE_TYPE as Record<string, PresetId>)[siteType]) || FALLBACK_PRESET_ID;
}
